const { gql } = require('@apollo/client');
const moment = require('moment');
const env = require('bench-environment').default;

const factory = (FdsConnectionModel, Collection, Api) => {
  const connectionFragment = gql`
    fragment connectionFragment on FinancialConnection {
      id
      financialInstitutionName
      state
      source
      institutionId
      isSyncing
      sourceId
      accounts {
        id
        name
        isPersonal
        isActive
        number
        type
        sourceSyncDateTime
        sourceId
        defaultCurrency
        isMultiCurrency
        isRemovedFromSource
        clientUsageType
        balances(toDate: $toDate, limit: 1, sortDesc: true) {
          data {
            amount
            sourceSyncTime
          }
        }
        transactions(toDate: $toDate, limit: 1, sortDesc: true) {
          data {
            date
          }
        }
        ledger {
          id
          name
          role
          latestValidBalance: balances(limit: 1, sortDesc: true, status: "VALID") {
            date
            amount
          }
          latestInvalidBalance: balances(limit: 1, sortDesc: true, status: "INVALID") {
            status
            date
            amount
          }
          referenceBalance: balances(limit: 1, sortDesc: true, status: "REFERENCE") {
            date
            amount
          }
        }
      }
    }
  `;

  class FdsConnectionCollection extends Collection {
    $fetch() {
      const query = gql`
        query FdsConnectionCollection($clientId: ID!, $toDate: String) {
          client(id: $clientId) {
            id
            financialConnections {
              ...connectionFragment
            }
          }
        }
        ${connectionFragment}
      `;
      const variables = { clientId: this.clientId, toDate: moment().format('YYYY-MM-DD') };
      const fetchPolicy = 'no-cache'; // always get fresh data
      return this.$graphqlClient
        .query({ query, variables, fetchPolicy })
        .then(res => {
          this.$set(res.data.client.financialConnections, { reset: true }); // always refresh
          return this.$cache();
        })
        .catch(() => {});
    }

    $deleteConnection(connectionId) {
      const mutation = gql`
        mutation deleteConnection($connectionId: ID!) {
          deleteFinancialConnection(connectionId: $connectionId)
        }
      `;

      const variables = {
        connectionId,
      };
      return this.$graphqlClient
        .mutate({ mutation, variables })
        .then(this.$removeById(connectionId));
    }

    connectAccountMutation(variables) {
      const mutation = gql`
        mutation newConnection(
          $clientId: ID!
          $source: String!
          $setupToken: String!
          $setupSessionId: String
        ) {
          setupNewConnection(
            clientId: $clientId
            source: $source
            setupToken: $setupToken
            setupSessionId: $setupSessionId
          ) {
            id
            financialInstitutionName
            state
            source
            accounts {
              id
              name
              isPersonal
              number
              type
            }
          }
        }
      `;
      return this.$graphqlClient.mutate({ mutation, variables }).then(res => {
        this.$add(res.data.setupNewConnection);
      });
    }

    $connectGustoAccount(clientId, gustoAccountId) {
      const variables = {
        clientId,
        setupToken: gustoAccountId,
        source: 'GUSTO',
      };
      return this.connectAccountMutation(variables);
    }

    $connectPlaid(clientId, source, setupToken, setupSessionId) {
      const variables = {
        clientId,
        source,
        setupToken,
        setupSessionId,
      };
      return this.connectAccountMutation(variables);
    }

    $getPlaidAccessToken(connectionId) {
      const mutation = gql`
        mutation FdsConnectionCollection($connectionId: ID!) {
          generateConnectionPublicToken(connectionId: $connectionId)
        }
      `;
      const variables = { connectionId };
      return this.$graphqlClient
        .mutate({ mutation, variables })
        .then(res => res.data.generateConnectionPublicToken);
    }

    $deleteGustoConnection(connectionId) {
      const mutation = gql`
        mutation deleteFinancialConnection($connectionId: ID!) {
          deleteFinancialConnection(connectionId: $connectionId)
        }
      `;
      const variables = {
        connectionId,
      };
      return this.$graphqlClient
        .mutate({ mutation, variables })
        .then(() => {
          this.$removeById(connectionId);
        })
        .catch(() => {});
    }

    $updateConnection(connectionId) {
      const mutation = gql`
        mutation update($connectionId: ID!, $toDate: String) {
          syncConnection(connectionId: $connectionId) {
            ...connectionFragment
          }
        }
        ${connectionFragment}
      `;
      const variables = { connectionId, toDate: moment().format('YYYY-MM-DD') };
      return this.$graphqlClient.mutate({ mutation, variables }).then(res => {
        this.$removeById(connectionId);
        this.$add(res.data.syncConnection);
      });
    }

    $resetConnection(connectionId) {
      const mutation = gql`
        mutation reset($connectionId: ID!, $toDate: String) {
          resetConnection(connectionId: $connectionId) {
            ...connectionFragment
          }
        }
        ${connectionFragment}
      `;
      const variables = { connectionId, toDate: moment().format('YYYY-MM-DD') };
      return this.$graphqlClient.mutate({ mutation, variables }).then(res => {
        this.$removeById(connectionId);
        this.$add(res.data.resetConnection);
      });
    }

    $updateLedgerMapping(connectionId, accountId, ledgerId, dryRun) {
      const mutation = gql`
        mutation FdsConnectionCollection($accountId: ID!, $ledgerId: ID, $dryRun: Boolean!) {
          updateAccountLedgerMapping(accountId: $accountId, ledgerId: $ledgerId, dryRun: $dryRun) {
            invalidatedBalances {
              ledgerId
              newLatestValidBalanceDate
              numBalancesInvalidated
            }
          }
        }
      `;
      const variables = {
        accountId,
        ledgerId,
        dryRun,
      };
      return this.$graphqlClient.mutate({ mutation, variables }).then(res => {
        this.$removeById(connectionId);
        this.$fetch();
        return res;
      });
    }

    $setIsPersonalFieldOfAccount(accountId, newState) {
      const mutation = gql`
        mutation updateAccountsIsPersonalField($accountId: ID!, $newState: Boolean!) {
          setAccountsIsPersonalField(accountId: $accountId, newState: $newState)
        }
      `;

      const variables = {
        accountId,
        newState,
      };
      return this.$graphqlClient.mutate({ mutation, variables }).then(() => {
        this.$fetch();
      });
    }

    $setClientUsageType(id, clientUsageType) {
      return fetch(env.location.v2GraphqlRoot, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variables: {
            input: {
              ids: [id],
              clientUsageType: clientUsageType,
            },
          },
          query: `
          mutation updateFinancialDataAccountClientUsage($input: UpdateFinancialDataAccountClientUsageInput!) {
            updateFinancialDataAccountClientUsage(input: $input) {
              ... on FinancialDataAccountsResponse {
                accounts {
                  id
                  name
                  clientUsageType
                }
              }
            }
          }
      `,
        }),
      })
        .then(res => res.json())
        .then(data => {
          return data;
        })
        .catch(error => {
          throw error;
        });
    }

    $fetchTransactionCsvForConnection(connectionId, startDate, toDate) {
      return this.$apiService.navigate(
        `/connection/${connectionId}/transactions/csv?from=${startDate}&to=${toDate}`,
      );
    }

    $fetchTransactionsForConnection(connectionId, params) {
      const query = gql`
        query FdsConnectionCollection(
          $clientId: ID!
          $fromDate: String
          $toDate: String
          $limit: Int
          $sortDesc: Boolean
        ) {
          client(id: $clientId) {
            id
            financialConnections {
              id
              accounts {
                id
                transactions(
                  toDate: $toDate
                  fromDate: $fromDate
                  limit: $limit
                  sortDesc: $sortDesc
                ) {
                  data {
                    date
                  }
                }
              }
            }
          }
        }
      `;
      const fetchPolicy = 'no-cache'; // always get fresh data
      const variables = Object.assign(params, { clientId: this.clientId });
      return (
        this.$graphqlClient
          .query({ query, variables, fetchPolicy })
          // return just the transactions for the specified connection
          .then(res =>
            res.data.client.financialConnections
              .filter(connection => connection.id === connectionId)[0]
              .accounts.map(account => account.transactions.data)
              .flat(),
          )
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error(error);
          })
      );
    }

    getAccounts() {
      return this.data.flatMap(connection =>
        connection.accounts.map(account => ({
          ...account,
          institution: {
            name: connection.financialInstitutionName,
            source: connection.source,
            state: connection.state,
          },
        })),
      );
    }

    getConnectionForAccount(accountId) {
      return this.data.find(connection =>
        connection.accounts.some(account => account.id === accountId),
      );
    }

    getAccountMappedToLedger(ledgerId) {
      return this.getAccounts().find(account => account.ledger && account.ledger.id === ledgerId);
    }
  }
  FdsConnectionCollection.prototype.$id = null;
  FdsConnectionCollection.prototype.$idName = 'clientId';
  FdsConnectionCollection.prototype.$type = FdsConnectionModel;
  FdsConnectionCollection.prototype.$apiService = new Api('fds');
  FdsConnectionCollection.prototype.$resource = 'fds';
  FdsConnectionCollection.prototype.$$cacheModels = true;
  FdsConnectionCollection.prototype.$$useCachedModels = true;

  return FdsConnectionCollection;
};

const moduleDeps = [require('data/model/fdsConnection').name, require('data/util').name];

module.exports = angular
  .module('bench.data.collection.fdsConnection', moduleDeps)
  .factory('FdsConnectionCollection', factory);
